import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ExportButton } from '@/components/common/ExportButton';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { fetchAcordaos } from '@/services/anatel';
import type { AnswerOut } from '@/services/types';

const PAGE_SIZE = 50;

/**
 * The collegiate track — ANATEL's third corpus, and the one where the obvious
 * analysis is measurably wrong.
 *
 * `voter_canonical` now resolves 34 raw strings to 22 Conselheiros, so the
 * corpus's 209 dissents can be attributed to named people for the first time.
 *
 * What it does NOT show is a dissent rate, and that is not caution — it was
 * tested. These minutes record only departures from unanimity, so a member who
 * appears rarely appears largely because they dissented. The correlation between
 * a member's recorded votes and their dissent rate is negative, and low-volume
 * members show a median rate roughly ninety times the high-volume ones. Ranked
 * by rate the table would order the minutes rather than the Conselheiros, so it
 * is ordered by COUNT and the rate is refused with its evidence.
 */
export function AnatelAcordaosPage() {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const [onlyDissent, setOnlyDissent] = useState(true);
  const [offset, setOffset] = useState(0);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['anatel', 'acordaos', onlyDissent, offset],
    queryFn: () => fetchAcordaos({ onlyWithDissent: onlyDissent, limit: PAGE_SIZE, offset }),
    placeholderData: keepPreviousData,
  });

  const number = new Intl.NumberFormat(i18n.language);

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  const stats = [
    { key: 'acordaos', value: data.corpus.acordaos },
    { key: 'votes', value: data.corpus.votes },
    { key: 'people', value: data.corpus.named_people },
    { key: 'dissents', value: data.corpus.dissent_rows },
  ];

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('anatel:acordaos.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('anatel:acordaos.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="anatel:acordaos.explainer" />

      <FieldGuide i18nKey="anatel:acordaos.guide" level={2} />

      <div className="flex justify-end">
        {/* The file exports dissent COUNTS, and carries the rate refusal with
            them — two numeric columns invite the division that refusal forbids. */}
        <ExportButton
          path="/api/v1/anatel/export/acordaos"
          label={t('anatel:acordaos.exportDissents')}
        />
      </div>

      <AsOfStrip asOf={data.as_of} />

      {/* Rendered only when the window actually skipped something -- computed
          from a number the payload already carries, so it disappears by itself
          if the corpus stays clean. Today it renders nothing. */}
      {data.corpus.dates_unparsed > 0 ? (
        <p className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-muted-foreground">
          {t('anatel:acordaos.datesUnparsed', {
            unparsed: number.format(data.corpus.dates_unparsed),
            total: number.format(data.corpus.acordaos),
          })}
        </p>
      ) : null}

      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.key} className="rounded-lg border bg-card p-3">
            <dt className="text-xs text-muted-foreground">{t(`anatel:acordaos.corpus.${stat.key}`)}</dt>
            <dd className="mt-1 text-xl font-semibold tabular-nums text-foreground">
              {number.format(stat.value)}
            </dd>
          </div>
        ))}
      </dl>

      {/* 97% of rows are PRESENT — an attendance mark. Stated up front, because
          a reader who assumes these are votes will misread everything below. */}
      <p className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
        {t('anatel:acordaos.presentWarning', {
          present: number.format(data.corpus.present_rows),
          votes: number.format(data.corpus.votes),
        })}
      </p>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('anatel:acordaos.dissenters.title')}
        </h2>
        <Answer answer={data.dissents} help={t('anatel:acordaos.dissenters.help')}>
          {(record) => (
            <div className="space-y-2">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">
                        {t('anatel:acordaos.dissenters.person')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('anatel:acordaos.dissenters.dissents')}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t('anatel:acordaos.dissenters.recorded')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {record.people.map((person) => (
                      <tr key={person.name} className="border-t">
                        <td className="px-3 py-2">
              {/* The slug comes off the payload. Folding the name here would be a
                  second implementation of a rule the backend already owns. */}
              <Link
                to={`/anatel/r/${person.slug}`}
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                {person.name}
              </Link>
            </td>
                        {/* Ordered and emphasised by COUNT. The recorded-votes
                            column sits beside it as context, never as a
                            denominator the reader is invited to divide by. */}
                        <td className="px-3 py-2 text-right font-medium tabular-nums text-foreground">
                          {number.format(person.dissents)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {number.format(person.recorded_votes)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {record.below_floor > 0 ? (
                <p className="text-xs text-muted-foreground">
                  {/* Not `count`: i18next reserves it as the plural selector and
                      types it as a number, so a formatted string fails to compile
                      and would opt this key into pluralisation rules besides. */}
                  {t('anatel:acordaos.dissenters.belowFloor', {
                    people: number.format(record.below_floor),
                  })}
                </p>
              ) : null}
            </div>
          )}
        </Answer>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">
          {t('anatel:acordaos.refusals.title')}
        </h2>
        <div className="grid gap-4 lg:grid-cols-2">
          <RefusalCard title={t('anatel:acordaos.refusals.rate')} answer={data.dissent_rate} />
          <RefusalCard
            title={t('anatel:acordaos.refusals.agreement')}
            answer={data.agreement_matrix}
          />
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-sm font-semibold text-foreground">
            {t('anatel:acordaos.list.title')}
          </h2>
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={onlyDissent}
              onChange={(event) => {
                setOnlyDissent(event.target.checked);
                setOffset(0);
              }}
              className="h-4 w-4 rounded border"
            />
            {t('anatel:acordaos.list.onlyDissent')}
          </label>
          <span className="ml-auto text-xs text-muted-foreground" aria-live="polite">
            {t('anatel:acordaos.list.showing', { total: number.format(data.total) })}
          </span>
        </div>

        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">{t('anatel:acordaos.list.process')}</th>
                <th className="px-3 py-2 font-medium">{t('anatel:acordaos.list.date')}</th>
                <th className="px-3 py-2 font-medium">{t('anatel:acordaos.list.relator')}</th>
                <th className="px-3 py-2 text-right font-medium">
                  {t('anatel:acordaos.list.dissents')}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item, index) => (
                <tr key={`${item.process_number ?? 'none'}-${index}`} className="border-t">
                  <td className="px-3 py-2">
                    {/* Linked only when the process exists in the PADO corpus. */}
                    {item.process_key ? (
                      <Link
                        to={`/anatel/p/${item.process_key}`}
                        className="font-medium text-primary underline-offset-4 hover:underline"
                      >
                        {item.process_number}
                      </Link>
                    ) : (
                      <span>{item.process_number ?? '—'}</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                    {item.reference_date_raw ?? '—'}
                  </td>
                  <td className="max-w-xs truncate px-3 py-2 text-muted-foreground">
                    {item.relator ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-foreground">
                    {item.dissents === 0 ? (
                      ''
                    ) : item.dissent_details.length > 0 ? (
                      <details className="group inline-block text-left align-top">
                        <summary className="inline cursor-pointer tabular-nums text-foreground marker:text-muted-foreground">
                          {number.format(item.dissents)}
                        </summary>
                        <div className="mt-1.5 max-w-sm space-y-1.5 whitespace-normal text-xs">
                          <p className="font-medium text-foreground">
                            {t('anatel:acordaos.list.dissentReasoning')}
                          </p>
                          {item.dissent_details.map((detail, detailIndex) => (
                            <p key={detailIndex} className="text-muted-foreground">
                              <span className="font-medium text-foreground">{detail.voter}:</span>{' '}
                              {detail.reasoning_excerpt}
                            </p>
                          ))}
                        </div>
                      </details>
                    ) : (
                      number.format(item.dissents)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(offset - PAGE_SIZE, 0))}
            className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
          >
            {t('anatel:decisions.previous')}
          </button>
          <button
            type="button"
            disabled={offset + PAGE_SIZE >= data.total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
            className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
          >
            {t('anatel:decisions.next')}
          </button>
        </div>
      </section>
    </div>
  );
}

function RefusalCard<T>({ title, answer }: { title: string; answer: AnswerOut<T> }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <h3 className="mb-2 text-sm font-medium text-foreground">{title}</h3>
      <Answer answer={answer}>{() => null}</Answer>
    </div>
  );
}
